import { getApiBaseUrl } from '@/lib/api-config'

/**
 * One row of `GET /api/tools/generations` — a signed-in visitor's permanent
 * tool-page history, shared by every studio and panel that renders one.
 *
 * ONE DECLARATION, NOT THREE. The two Seedance hooks and the generic fal hook
 * each carried their own copy of this shape and their own fetch around it, so
 * a field the endpoint gained reached whichever copies someone remembered to
 * edit. `deliveryMode` is what that cost: the copies drifted into documenting
 * the field as one the server does not send, and a reloaded free preview lost
 * its watermark notice and re-enabled a handoff that can only 404.
 */
export interface ToolFailure {
  code: string
  message: string
  nextStep: string
}

/** The credit statement is based on a settled server response, never a timeout. */
export function toolFailureMessage(
  failure: ToolFailure | null | undefined,
  deliveryMode: string | null | undefined,
  requestId: string,
  historical = false,
): string {
  const explanation = failure
    ? `${failure.message} ${failure.nextStep}`
    : 'Generation failed without a specific reason. If this keeps happening, contact support with the request ID.'
  const settlement =
    deliveryMode === 'preview'
      ? historical
        ? 'No credits were spent on this preview.'
        : 'No credits were spent. Your free preview is available again.'
      : 'Your credits have been refunded.'
  return `${explanation} ${settlement} Request ID: ${requestId}`
}

export interface StoredGeneration {
  status?: 'COMPLETED' | 'FAILED'
  failure?: ToolFailure | null
  id: string
  toolSlug: string
  requestId: string
  outputKind: string
  prompt: string | null
  /** Our own permanent serve path — never a provider URL. */
  url: string
  createdAt: string
  /**
   * Which bytes `url` hands out. Optional because a pod still running the
   * previous release omits it; read it through `historyDeliveryMode`, which
   * treats absent as `'full'` — what this list meant before the field existed.
   */
  deliveryMode?: 'full' | 'preview' | null
}

/**
 * This user's permanently-stored generations, for one tool or across all of
 * them.
 *
 * Replaces a sessionStorage list that died with the browser tab and held at
 * most twelve entries whose URLs expired anyway. Returns [] on any failure —
 * history is additive context, never the thing standing between a visitor and
 * their result.
 *
 * `toolSlug` is nullable because the Creatives landing needs the cross-tool
 * view: the endpoint's filter has always been optional.
 */
export async function loadStoredGenerations(
  token: string,
  toolSlug: string | null,
  limit = 24,
  includeFailed = false,
): Promise<StoredGeneration[]> {
  try {
    const query = new URLSearchParams({ limit: String(limit) })
    if (includeFailed) query.set('includeFailed', 'true')
    if (toolSlug) query.set('toolSlug', toolSlug)
    // web-arch-allow-direct-fetch: imperative tool-page history load.
    const res = await fetch(
      `${getApiBaseUrl()}/api/tools/generations?${query.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (!res.ok) return []
    const body = (await res.json()) as { data?: StoredGeneration[] }
    return Array.isArray(body.data) ? body.data : []
  } catch {
    return []
  }
}

/**
 * The delivery mode to render a restored history entry with.
 *
 * Only an explicit `'preview'` withholds the handoff. Absent and `null` both
 * mean the full delivery, which is the answer that matches a paid run and the
 * answer an older pod's response implies.
 */
export function historyDeliveryMode(
  generation: StoredGeneration,
): 'full' | 'preview' {
  return generation.deliveryMode === 'preview' ? 'preview' : 'full'
}
